import { type ReactNode, useId } from 'react';

import { useDark, useReducedMotion, useWidth } from '../lib/appearance';

/**
 * The landscape behind Today's cairn, painted from the phone's own path text
 * (`TrailScene.swift`): a sky, the old ridge of the first mark, a hill, and
 * near ground in the page's colour, so the scene runs into the page below it
 * without an edge. Drawn 402 wide and 262 tall; it stretches to its width and
 * keeps its ground at the bottom, so a shorter scene shows less sky.
 */

export type Sky = 'dawn' | 'day' | 'dusk' | 'night';

/** Dark is always night; in light the sky follows the workspace's clock. */
export function skyAt(hour: number, dark: boolean): Sky {
  if (dark) {
    return 'night';
  }

  if (hour >= 5 && hour < 10) {
    return 'dawn';
  }

  return hour >= 10 && hour < 17 ? 'day' : 'dusk';
}

const sun: Record<Sky, { y: number; r: number }> = {
  dawn: { y: 150, r: 30 },
  day: { y: 116, r: 28 },
  dusk: { y: 176, r: 34 },
  night: { y: 104, r: 19 },
};

const ridge =
  'M0 246 C60 244 130 240 180 228 C206 221 222 196 236 168 L246 146 C250 139 256 139 260 146 L292 196 C296 203 304 204 310 198 L354 152 C358 148 364 148 368 152 L402 180 L402 262 L0 262 Z';

const hill =
  'M0 252 C80 249 150 244 204 236 C244 230 276 216 312 214 C346 212 378 218 402 222 L402 262 L0 262 Z';

const ground =
  'M0 262 L0 258 C80 255 160 250 226 242 C266 237 296 232 324 232 C356 232 384 234 402 237 L402 262 Z';

const trail = 'M20 260 C92 257 166 251 226 245 C262 241 290 237 304 235';

const tufts =
  'M352 233 l-2.2 -6 M355 233 l0 -8 M358 233 l2.2 -6 M268 240 l-1.8 -5 M271 240 l0.4 -6.5 M274 240 l2 -4.6';

const birds = 'M186 74 q4 -4 8 0 q4 -4 8 0 M206 64 q3 -3 6 0 q3 -3 6 0';

const stem = 'M284 238 q1 -9 -1 -15';

const stars: [number, number, number, number][] = [
  [34, 30, 0.9, 0.7],
  [72, 18, 0.6, 0.5],
  [118, 44, 1.1, 0.8],
  [160, 24, 0.7, 0.45],
  [204, 58, 0.9, 0.7],
  [236, 22, 0.6, 0.5],
  [270, 70, 1.2, 0.85],
  [292, 40, 0.7, 0.5],
  [350, 64, 0.8, 0.6],
  [378, 28, 1, 0.75],
  [388, 110, 0.6, 0.4],
  [214, 104, 0.7, 0.45],
  [150, 84, 0.6, 0.35],
  [262, 122, 0.8, 0.5],
  [382, 150, 0.7, 0.45],
  [96, 70, 0.6, 0.4],
  [18, 92, 0.7, 0.4],
  [330, 18, 0.6, 0.5],
];

/** Where the cairn stands: across, as a share of the width, and above the bottom edge. */
export const cairnX = 320 / 402;
export const groundAboveBottom = 30;

export function TrailScene({
  sky,
  height,
  flower = false,
  className,
  children,
}: {
  sky: Sky;
  height: number;
  /** A small flower by the trail, for the morning the owner comes back after days away. */
  flower?: boolean;
  className?: string;
  /** Laid over the scene, such as the cairn standing on its hill. */
  children?: ReactNode;
}) {
  const [measure, width] = useWidth<HTMLDivElement>();
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const reduced = useReducedMotion();
  const sx = width / 402;
  const dy = height - 262;
  const place = `matrix(${sx} 0 0 1 0 ${dy})`;
  const { y, r } = sun[sky];
  const sunX = 318 * sx;
  const sunY = y + dy;

  return (
    <div
      ref={measure}
      className={className ? `trail-scene ${className}` : 'trail-scene'}
      data-sky={sky}
      style={{ height }}
    >
      {width > 0 && (
        <svg width={width} height={height} aria-hidden="true">
          <defs>
            <linearGradient
              id={`sky${id}`}
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="0"
              y2={Math.max(1, 200 + dy)}
            >
              <stop offset="0" className="sky-top" />
              <stop offset="1" className="sky-horizon" />
            </linearGradient>
            <radialGradient
              id={`glow${id}`}
              gradientUnits="userSpaceOnUse"
              cx={sunX}
              cy={sunY}
              r={r + 30}
            >
              <stop offset={(r * 0.8) / (r + 30)} className="moon-glow-inner" />
              <stop offset="1" className="moon-glow-outer" />
            </radialGradient>
          </defs>
          <rect width={width} height={height} fill={`url(#sky${id})`} />
          {sky === 'night' ? (
            <>
              {stars
                .filter(([, sy]) => sy + dy > 4)
                .map(([x, sy, radius, opacity]) => (
                  <circle
                    key={`${x}-${sy}`}
                    className={reduced ? 'star' : 'star twinkle'}
                    cx={x * sx}
                    cy={sy + dy}
                    r={radius}
                    style={{ opacity, animationDelay: `${(x * 37) % 3000}ms` }}
                  />
                ))}
              <circle cx={sunX} cy={sunY} r={r + 30} fill={`url(#glow${id})`} />
              <circle className="scene-sun" cx={sunX} cy={sunY} r={r} />
              <circle className="scene-moon-shadow" cx={sunX + 8} cy={sunY - 6} r={r - 2} />
            </>
          ) : (
            <>
              <circle className="scene-halo" cx={sunX} cy={sunY} r={r + 18} />
              <circle className="scene-sun" cx={sunX} cy={sunY} r={r} />
              <path
                className={reduced ? 'scene-birds' : 'scene-birds drifting'}
                d={birds}
                transform={`matrix(${sx} 0 0 1 0 ${Math.max(dy, -48)})`}
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
          <path className="scene-ridge" d={ridge} transform={place} />
          <path className="scene-hill" d={hill} transform={place} />
          <path className="scene-ground" d={ground} transform={place} />
          <path
            className="scene-trail"
            d={trail}
            transform={place}
            vectorEffect="non-scaling-stroke"
          />
          <path
            className="scene-tufts"
            d={tufts}
            transform={place}
            vectorEffect="non-scaling-stroke"
          />
          {flower && (
            <>
              <path
                className="scene-stem"
                d={stem}
                transform={place}
                vectorEffect="non-scaling-stroke"
              />
              <circle className="scene-bloom" cx={283 * sx} cy={221 + dy} r={3.2} />
              <circle className="scene-bloom-heart" cx={283 * sx} cy={221 + dy} r={1.3} />
            </>
          )}
        </svg>
      )}
      {children && (
        <div
          className="trail-scene-stand"
          style={{ left: `${cairnX * 100}%`, bottom: groundAboveBottom - 10 }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** The sky for now, in the workspace's zone and the page's appearance. */
export function useSky(hour: number) {
  return skyAt(hour, useDark());
}

const walk = (t: number, width: number) => ({
  x: 8 + (width - 26) * t,
  y: 22 + Math.sin(t * Math.PI * 2.2) * 7,
});

function walkPath(to: number, width: number) {
  return Array.from({ length: 49 }, (_, step) => {
    const { x, y } = walk((to * step) / 48, width);

    return `${step ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
}

/**
 * A running session drawn as a walk: from where the stretch began to a rest
 * spot fifty minutes on, with the walker partway along. Reaching it suggests
 * a rest and nothing more; the clock keeps recording.
 */
export function TrailProgress({ fraction, running }: { fraction: number; running: boolean }) {
  const [measure, width] = useWidth<HTMLDivElement>();
  const t = Math.min(Math.max(fraction, 0), 1);
  const walker = walk(t, width);
  const flag = walk(1, width);

  return (
    <div ref={measure} className="trail-progress" aria-hidden="true">
      {width > 0 && (
        <>
          <svg width={width} height={44} aria-hidden="true">
            <path className="trail-ahead" d={walkPath(1, width)} />
            <path className="trail-behind" d={walkPath(Math.max(t, 0.004), width)} />
            <path
              className="trail-pole"
              d={`M${flag.x + 6} ${flag.y} L${flag.x + 6} ${flag.y - 18}`}
            />
            <path
              className="trail-flag"
              d={`M${flag.x + 6} ${flag.y - 17.5} h12 l-2.5 4 l2.5 4 h-12 Z`}
            />
          </svg>
          <span
            className={running ? 'trail-walker running' : 'trail-walker'}
            style={{ left: walker.x, top: walker.y }}
          />
        </>
      )}
    </div>
  );
}
